import React, { useEffect, useRef, useState } from "react";
import { fileIcon as FileGlyph, videoFileIcon as VideoFileGlyph } from "assets/icons";
import { fetchLessonById } from "../../services/lessonService/lessonService";
import { useFileProgressPolling } from "../../hooks/useFileProgressPolling";
import { LessonDetailsProps } from "./LessonDetails.types";
import {
  DetailsGrid,
  EmptyValue,
  FileIconBox,
  FileMain,
  FileMeta,
  FileName,
  FileRow,
  FilesList,
  LessonDetailsContainer,
  MaterialLink,
  MaterialsColumn,
  ProgressBar,
  ProgressTrack,
  ReadBlock,
  ReadLabel,
  ReadText,
  SectionTitle,
  VideoPlayersContainer,
} from "./LessonDetails.styles";
import AssignmentsContainer from "../AssignmentsContainer";
import VideoPlayer from "../VideoPlayer/VideoPlayer";
import { FileDisplay } from "../FileToDisplay/FileToDisplay.types";

const getFileMeta = (file: FileDisplay) => {
  if (file.type === "video" && file.status === "processing") {
    return file.progress_percent !== undefined
      ? `Обработка видео, ${file.progress_percent}%`
      : "Обработка видео";
  }

  if (file.type === "video" && file.status === "failed") {
    return "Ошибка обработки видео";
  }

  if (file.type === "video") {
    return "Видео";
  }

  return "Файл";
};

const LessonDetails: React.FC<LessonDetailsProps> = ({
  content,
  category,
  video_url,
  files_from_storage,
  courseId,
  lessonId,
  isSubscriptionActive,
}) => {
  const [storageFiles, setStorageFiles] = useState<FileDisplay[]>(files_from_storage || []);
  const videoRefs = useRef<Map<number, HTMLVideoElement | null>>(new Map());

  const handleVideoPlay = (playingVideoId: number) => {
    videoRefs.current.forEach((videoElement, videoId) => {
      if (videoId !== playingVideoId && videoElement && !videoElement.paused) {
        videoElement.pause();
      }
    });
  };

  useFileProgressPolling({
    isActive: true,
    entityId: lessonId,
    filesFromStorage: storageFiles,
    updateFilesCallback: (files) => {
      setStorageFiles(files.map((file) => ({ ...file })));
    },
    fetchFunction: fetchLessonById,
    fetchParams: [courseId, lessonId],
  });

  useEffect(() => {
    const currentIds = storageFiles.map((file) => file.id).sort().join(",");
    const nextIds = (files_from_storage || []).map((file) => file.id).sort().join(",");

    if (currentIds !== nextIds || storageFiles.length !== (files_from_storage || []).length) {
      setStorageFiles(files_from_storage || []);
    }
  }, [files_from_storage, storageFiles]);

  const readyVideos = storageFiles.filter(
    (file) => file.type === "video" && file.status === "ready",
  );

  return (
    <LessonDetailsContainer data-expanded-lesson-panel>
      <DetailsGrid>
        <div>
          <ReadBlock>
            <ReadLabel>Описание</ReadLabel>
            {content ? <ReadText>{content}</ReadText> : <EmptyValue>не заполнено</EmptyValue>}
          </ReadBlock>

          <ReadBlock>
            <ReadLabel>Категория</ReadLabel>
            {category ? <ReadText>{category}</ReadText> : <EmptyValue>без категории</EmptyValue>}
          </ReadBlock>

          <ReadBlock>
            <ReadLabel>Ссылка на материал</ReadLabel>
            {video_url ? (
              <ReadText>
                <MaterialLink href={video_url} target="_blank" rel="noopener noreferrer">
                  {video_url}
                </MaterialLink>
              </ReadText>
            ) : (
              <EmptyValue>не добавлена</EmptyValue>
            )}
          </ReadBlock>
        </div>

        <MaterialsColumn>
          <SectionTitle>Файлы и видео</SectionTitle>
          {storageFiles.length > 0 ? (
            <FilesList>
              {storageFiles.map((file) => {
                const isProcessing = file.type === "video" && file.status === "processing";

                return (
                  <FileRow key={file.id}>
                    <FileIconBox>
                      {file.type === "video" ? (
                        <VideoFileGlyph aria-hidden="true" />
                      ) : (
                        <FileGlyph aria-hidden="true" />
                      )}
                    </FileIconBox>
                    <FileMain>
                      <FileName title={file.name}>{file.name}</FileName>
                      <FileMeta>{getFileMeta(file)}</FileMeta>
                      {isProcessing && file.progress_percent !== undefined && (
                        <ProgressTrack aria-hidden="true">
                          <ProgressBar $progress={file.progress_percent} />
                        </ProgressTrack>
                      )}
                    </FileMain>
                  </FileRow>
                );
              })}
            </FilesList>
          ) : (
            <EmptyValue>Материалов нет</EmptyValue>
          )}

          {readyVideos.length > 0 && (
            <VideoPlayersContainer>
              {readyVideos.map((file) => (
                <VideoPlayer
                  key={file.id}
                  lessonId={lessonId}
                  videoId={file.id}
                  courseId={courseId}
                  videoName={file.name}
                  onVideoRef={(ref) => {
                    if (ref) {
                      videoRefs.current.set(file.id, ref);
                    } else {
                      videoRefs.current.delete(file.id);
                    }
                  }}
                  onPlay={() => handleVideoPlay(file.id)}
                />
              ))}
            </VideoPlayersContainer>
          )}
        </MaterialsColumn>
      </DetailsGrid>

      <AssignmentsContainer
        courseId={courseId}
        lessonId={lessonId}
        isSubscriptionActive={isSubscriptionActive}
      />
    </LessonDetailsContainer>
  );
};

export default LessonDetails;
